"use client";

import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { SLIDE_W, SLIDE_H, type SpeciesSlideData } from "@/lib/carousel/types";
import { speciesNameLayout, type SpeciesNameBox } from "@/lib/carousel/templates/field-diary";

export interface FieldDiarySpeciesOverlayProps {
  data: SpeciesSlideData;
  fonts: { display: string; body: string };
  update: (patch: Partial<{ namePrimary: string; nameSecondary: string; nameTertiary: string }>) => void;
}

function boxStyle(box: SpeciesNameBox, fonts: { display: string; body: string }): CSSProperties {
  return {
    position: "absolute",
    left: box.left,
    bottom: box.bottom,
    height: box.height,
    width: SLIDE_W - box.left * 2,
    fontFamily: box.family === "display" ? fonts.display : fonts.body,
    fontSize: box.fontSize,
    fontWeight: box.fontWeight,
    color: box.color,
  };
}

/**
 * Real HTML <input>s positioned pixel-for-pixel over the Field Diary species
 * card's canvas-drawn text (via speciesNameLayout — the single source of
 * truth field-diary.ts's drawSpecies also uses) so the underlying canvas
 * can skip painting them. Click the name on the slide, type — no bottom
 * panel round-trip. Boxes only appear for fields that currently have text,
 * matching the export exactly; use the bottom panel to add a new (empty)
 * primary/secondary name for the first time.
 */
export default function FieldDiarySpeciesOverlay({ data, fonts, update }: FieldDiarySpeciesOverlayProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  // .ced-stage-card is a fixed 360px wide (app/globals.css) — used as the
  // first-paint fallback so the overlay is never at scale 0 (unclickable,
  // sitting collapsed at the top-left corner) for the frame before the
  // ResizeObserver below gets a real measurement.
  const [scale, setScale] = useState(360 / SLIDE_W);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      if (el.clientWidth > 0) setScale(el.clientWidth / SLIDE_W);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { tertiary, secondary, primary } = speciesNameLayout(data);

  return (
    <div ref={wrapRef} className="ced-text-overlay">
      <div
        className="ced-text-overlay-inner"
        style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})` }}
      >
        {tertiary && (
          <input
            type="text"
            className="ced-text-overlay-input"
            style={boxStyle(tertiary, fonts)}
            value={data.nameTertiary}
            onChange={(e) => update({ nameTertiary: e.target.value })}
            placeholder="Name"
            aria-label="Name"
          />
        )}
        {secondary && (
          <input
            type="text"
            className="ced-text-overlay-input"
            style={boxStyle(secondary, fonts)}
            value={data.nameSecondary ?? ""}
            onChange={(e) => update({ nameSecondary: e.target.value })}
            aria-label="Secondary name"
          />
        )}
        {primary && (
          <input
            type="text"
            className="ced-text-overlay-input"
            style={boxStyle(primary, fonts)}
            value={data.namePrimary ?? ""}
            onChange={(e) => update({ namePrimary: e.target.value })}
            aria-label="Primary name"
          />
        )}
      </div>
    </div>
  );
}
